import React, {useCallback, useEffect, useState} from 'react'
import {Box, Typography} from '@mui/material'
import moment from 'moment'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import AppDialog from 'src/components-new/feedback/AppDialog'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import getFileMIMEtype from 'src/components-new/feedback/getFileMIMEtype'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import {docTypesOptions, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import DocumentUploaderNew from 'src/pages-new/patient/tabs/documents/DocumentUploaderNew'
import DocumentInfoDialogNew from 'src/pages-new/patient/tabs/documents/DocumentInfoDialogNew'

interface DocumentsTabProps {
  patientId: string
  showUpload?: boolean
}

interface DocumentRow {
  _id: string
  fileId?: string
  fileName?: string
  name?: string
  category?: string[]
  categoryReadable?: string
  uploadDate?: string
  type?: string
  description?: string
  size?: number
  creatorId?: string
  user?: {firstName?: string; lastName?: string}
  [key: string]: any
}

interface DocumentColumnConfig {
  id: string
  label: string
}

const DEFAULT_COLUMNS: DocumentColumnConfig[] = [
  {id: 'categoryReadable', label: 'CATEGORIA'},
  {id: 'uploadDate', label: 'CARICATO IL'},
  {id: 'name', label: 'NOME'},
  {id: 'type', label: 'TIPO'},
]

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

function renderCell(id: string) {
  return (row: DocumentRow): React.ReactNode => {
    if (id === 'uploadDate') {
      return <Typography variant="body2">{row.uploadDate ? moment(row.uploadDate).format('DD MMMM YYYY, HH:mm') : '—'}</Typography>
    }
    if (id === 'categoryReadable') {
      return <Typography variant="body2">{row.categoryReadable ?? '—'}</Typography>
    }
    if (id === 'name') {
      return <Typography variant="body2">{row.name || row.fileName || '—'}</Typography>
    }
    return <Typography variant="body2">{row[id] ?? '—'}</Typography>
  }
}

/**
 * New-style port of src/components/templates/PatientDetailsComponent/Documents/Documents.tsx.
 * Row click (handlePreview) opens FilePreviewDialog when the mime is recognized,
 * falling back to a forced download for unrecognized extensions. The "Scarica"
 * row-action (handleDownload) always force-downloads the file via a synthetic
 * anchor click, regardless of mime — it must never open the preview.
 */
export default function DocumentsTab({patientId, showUpload = true}: DocumentsTabProps): JSX.Element {
  const documentsConfigurations = useAppSelector(state => state.pspConfigReducer.data.documentsConfigurations)
  const patientDocumentsColumnOptions: DocumentColumnConfig[] = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.patientDocumentsConfiguration?.columnOptions
  )
  const loggedUser = useAppSelector(({userReducer}) => userReducer.data)

  const categoryOptions: {value: string; label: string}[] = documentsConfigurations?.categoryOptions?.categoriesFilters ?? []

  const [rows, setRows] = useState<DocumentRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [statusDelete, setStatusDelete] = useState<string | undefined>()
  const [statusPatch, setStatusPatch] = useState<string | undefined>()
  const [statusDownload, setStatusDownload] = useState<string | undefined>()
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)

  const [infoDoc, setInfoDoc] = useState<DocumentRow | null>(null)
  const [deleteDoc, setDeleteDoc] = useState<DocumentRow | null>(null)

  const parseCategoryToLabel = (value?: string): string | undefined =>
    categoryOptions.find(c => c.value === value)?.label

  const parseCategories = (category?: string[]): string | undefined => {
    if (category) {
      if (category.length > 0) return category.map(c => parseCategoryToLabel(c)).join(', ')
      return '-'
    }
    return undefined
  }

  const fetchData = useCallback(async () => {
    const fetched: DocumentRow[] = (await apiClient.getPatientsDocuments(patientId, filters, setStatus, pagination)) ?? []
    const parsed = fetched.map(doc => ({
      ...doc,
      categoryReadable: parseCategories(doc.category),
      type: docTypesOptions.find((t: any) => t.value === doc.type)?.label,
    }))
    setRows(parsed)
    const cnt = (await apiClient.getPatientsDocumentsCount(patientId, filters, setStatusCount)) ?? 0
    setCount(Number(cnt))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleDownload = async (row: DocumentRow) => {
    if (!row.fileId) return
    const extension = row.fileName?.includes('.') ? row.fileName.split('.').pop() ?? '' : ''
    const downloadName = `${row.user?.firstName}_${row.user?.lastName}_${parseCategoryToLabel(row.category?.[0]) ?? ''}.${extension}`

    const fileResponse = await apiClient.patientsDownload(patientId, row.fileId, setStatusDownload)
    if (!fileResponse) return
    const blob = new Blob([new Uint8Array(fileResponse)], {type: ''})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = downloadName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const handlePreview = async (row: DocumentRow) => {
    if (!row.fileId) return
    const mime = getFileMIMEtype(row.fileName)

    if (mime === undefined) {
      await handleDownload(row)
      return
    }

    const extension = row.fileName?.includes('.') ? row.fileName.split('.').pop() ?? '' : ''
    const downloadName = `${row.user?.firstName}_${row.user?.lastName}_${parseCategoryToLabel(row.category?.[0]) ?? ''}.${extension}`
    setViewerState({open: true, filePath: '', mime, fileName: downloadName, status: STATUS.PENDING})
    const fileResponse = await apiClient.patientsDownload(patientId, row.fileId, setStatusDownload)
    if (!fileResponse) return
    const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState(prev => ({...prev, filePath: url, status: STATUS.FULFILLED}))
  }

  const handleSave = async (patch: {category: string[]; name: string; description: string}) => {
    if (!infoDoc) return
    await apiClient.patchPatientsDocuments(patientId, infoDoc._id, patch, setStatusPatch)
    await fetchData()
    setInfoDoc(null)
  }

  const handleDelete = async () => {
    if (!deleteDoc) return
    await apiClient.deletePatientsDocument(patientId, deleteDoc._id, setStatusDelete)
    await fetchData()
    setDeleteDoc(null)
  }

  const handleSubmitFile = async (
    file: File,
    metadata: {category: string[]; name?: string},
    setUploadStatus: (s: string) => void
  ) => {
    const formData = new FormData()
    metadata.category.forEach(c => formData.append('category', c))
    formData.append('name', metadata.name || file.name)
    formData.append('file', file, file.name)
    await apiClient.postPatientsDocuments(patientId, formData, setUploadStatus)
  }

  const columnConfig = patientDocumentsColumnOptions?.length > 0 ? patientDocumentsColumnOptions : DEFAULT_COLUMNS

  const columns: ColumnDef<DocumentRow>[] = [
    ...columnConfig.map(col => ({
      key: col.id,
      header: col.label,
      hideInMobileCard: col.id === 'name',
      render: renderCell(col.id),
    })),
    {
      key: 'actions',
      header: '',
      width: 112,
      isActionColumn: true,
      render: (row: DocumentRow) => (
        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          <ActionIconButton variant="info" tooltip="Info file" onClick={() => setInfoDoc(row)} />
          <ActionIconButton variant="download" tooltip="Scarica" onClick={() => handleDownload(row)} />
          {row.creatorId === loggedUser?.authUserId && (
            <ActionIconButton variant="delete" tooltip="Cancella" onClick={() => setDeleteDoc(row)} />
          )}
        </Box>
      ),
    },
  ]
  // Only show the mobile card header when "name" survived the PSP's columnOptions
  // (or no config was set at all) — otherwise a disabled column would still show up there.
  const hasNameColumn = columns.some(col => col.key === 'name')

  const filterConfig: FilterItem[] = [
    {id: 'category', label: 'Categoria', type: 'select', options: categoryOptions},
    {id: 'rangeDates', label: 'Data inserimento', type: 'rangePicker'},
  ]

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2}}>
        {showUpload && <DocumentUploaderNew onSubmitFile={handleSubmitFile} onSubmitRefresh={fetchData} />}
        <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare i documenti.'}
        </InlineMessage>
      )}

      {statusPatch === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusPatch(undefined)} sx={{mb: 2}}>
          {'Errore nel modificare il documento.'}
        </InlineMessage>
      )}

      {statusDelete === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDelete(undefined)} sx={{mb: 2}}>
          {'Errore nel cancellare il documento.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nel scaricare il documento.'}
        </InlineMessage>
      )}

      <DataTable<DocumentRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun documento trovato"
        onRowClick={handlePreview}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        mobileCardTitle={hasNameColumn ? (row) => row.name || row.fileName || '—' : undefined}
      />

      <DocumentInfoDialogNew
        open={infoDoc !== null}
        document={infoDoc ?? undefined}
        categoryOptions={categoryOptions}
        readOnly={infoDoc?.creatorId !== loggedUser?.authUserId}
        onClose={() => setInfoDoc(null)}
        onSave={handleSave}
      />

      <AppDialog
        open={deleteDoc !== null}
        onClose={() => setDeleteDoc(null)}
        title="Conferma eliminazione documento"
        actions={
          <>
            <Button variant="secondary" onClick={() => setDeleteDoc(null)}>
              {'Annulla'}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              {'Ok'}
            </Button>
          </>
        }
      >
        <Typography sx={{pt: 1}}>{'Vuoi eliminare questo documento?'}</Typography>
      </AppDialog>

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />
    </Box>
  )
}
